import { assertUnreachable } from '@disy/lib/custom-error';
import type { Icon, IconOptions } from '@disy/ui/icon';
import { getSetting } from '@disy/workbook.lib/settings';

import type { LayerLabelType } from 'cadenza/ui/layer-label/layer-label';
import { layerIcon } from 'cadenza/ui/layer-label/layer-label';
import type { MapVisualizationKey } from 'cadenza/workbook/api/data-view-visualization-key';
import { getWorkbookLayerType } from 'cadenza/workbook/map/workbook-layer-type-util';
import { LayerSettingsKey } from 'cadenza/workbook/model/layer-settings-key.gen';
import type { LayerData } from 'cadenza/workbook/ui/data-view-layer-label/data-view-layer-label';

export function createLayerIcon(layerData: Omit<LayerData, 'node'>, options?: IconOptions): Icon {
  const layerType = getLayerLabelType(layerData);
  return layerIcon(layerType, options);
}

export function getLayerLabelType(layerData: Omit<LayerData, 'node'>): LayerLabelType | MapVisualizationKey {
  const workbookLayerType = getWorkbookLayerType(layerData.definition);
  switch (workbookLayerType) {
    case 'STANDARD_GROUP':
    case 'TOGGLE_GROUP':
      return 'group';
    case 'DATA_VIEW':
      return layerData.dataView!.visualizationKey as MapVisualizationKey;
    case 'SKETCH':
      return 'sketch';
    case 'EXTERNAL_WMS':
    case 'EXTERNAL_WMTS':
    case 'EXTERNAL_VECTOR_TILE':
    case 'EXTERNAL_ARCGIS_MAP':
    case 'EXTERNAL_ARCGIS_IMAGE':
      return 'external';
    case 'CLASSIC': {
      // classic layers from XML repo, sketch layers, etc.
      const type = getSetting<string>(layerData.definition, LayerSettingsKey.toPersistenceKey('LAYER_TYPE'))!;
      return CLASSIC_LAYER_VISUALIZATION_TYPE_TO_LAYER_TYPE[type] ?? 'raster-layer';
    }
    default:
      assertUnreachable(workbookLayerType);
  }
}

const CLASSIC_LAYER_VISUALIZATION_TYPE_TO_LAYER_TYPE: Record<string, LayerLabelType> = {
  points: 'point',
  lines: 'line',
  heatmap: 'heatmap',
  polygons: 'polygon',
};
